import { inject, Injectable, Injector } from '@angular/core';
import { AgentCategory } from '../models/agent-config.model';
import { AiClientService } from './ai-client.service';
import { AiConversationItem, TokenUsage } from '../transports/ai-transport';
import { DiskService } from '../../services/disk/disk.service';

/** Disk envelope for a cached summary conversation. */
interface CachedConversation {
  /** Foundry conversation id the items belong to. */
  convId: string;
  /** Full ordered turn list (chronological) as returned by listItems(). */
  items: AiConversationItem[];
  /** Last cache-write timestamp, ISO. */
  at: string;
  /** Signature of the grounding this reply was generated from, when the caller supplies one.
   *  Lets a caller tell whether the cached reply still matches the current grounding. */
  signature?: string;
  /** Feedback-correlation metadata for the reply this entry holds — a cache hit produces no new
   *  tokens, so callers that need to show usage/feedback on a cached reply read these back
   *  instead of re-deriving them. */
  responseId?: string;
  usage?: TokenUsage;
}

/** When the cache was last written, the grounding signature, and the feedback metadata it was
 *  written with. */
export interface CacheMeta {
  at: string | null;
  signature: string | null;
  responseId: string | null;
  usage: TokenUsage | null;
}

/** Feedback-correlation metadata a caller supplies alongside a cache write. */
export interface CacheFeedbackMeta {
  signature?: string;
  responseId?: string;
  usage?: TokenUsage;
}

/**
 * Shared cache + resolver for summary conversations (meeting + record).
 *
 * The conversation IS the data: transcript, analysis, and follow-up turns are
 * all just items inside the Foundry conversation. This service persists the
 * whole item list keyed by an arbitrary cache key and resolves it through one
 * ladder — local cache first, else pull from Foundry by conv id and cache it,
 * else none. Caching on every Foundry fetch is the single rule that makes
 * cross-system "download & cache locally" automatic. Callers derive transcript
 * / analysis / history from the items by shape-peel (feature-specific).
 */
@Injectable({ providedIn: 'root' })
export class SummaryConversationCacheService {
  readonly #disk = inject(DiskService);
  // AiClientService is resolved LAZILY (via Injector) to break a DI cycle:
  // AiClientService → FoundryTransport → AiToolRegistry → tool providers
  // (MeetingTools) → this cache → AiClientService. We only need the client
  // inside resolve()'s Foundry-fallback branch, never at construction, so a
  // deferred lookup is safe and keeps the eager graph acyclic.
  readonly #injector = inject(Injector);
  #aiClient: AiClientService | null = null;
  get #ai(): AiClientService {
    return (this.#aiClient ??= this.#injector.get(AiClientService));
  }

  /** Single disk read of the cache envelope; null on miss or read error. */
  async #readDoc(cacheKey: string): Promise<{ convId?: string; items?: unknown; at?: string; signature?: string; responseId?: string; usage?: TokenUsage } | null> {
    if (!cacheKey) return null;
    try {
      const doc: any = await this.#disk.retrieve(cacheKey, true);
      return doc?.raw ?? null;
    } catch (e) {
      console.error('SummaryConversationCache readDoc:', e);
      return null;
    }
  }

  /**
   * Locally cached items for a key, or null when absent / pre-refactor shape.
   * Private: all reads go through `resolve()` so the local→Foundry ladder can't
   * be bypassed by a caller reaching for the cache directly.
   */
  async #getCachedItems(cacheKey: string): Promise<AiConversationItem[] | null> {
    const raw = await this.#readDoc(cacheKey);
    return Array.isArray(raw?.items) ? (raw!.items as AiConversationItem[]) : null;
  }

  /** Reads just the cached conv id (used by callers whose id lives in the cache). */
  async getCachedConvId(cacheKey: string): Promise<string | null> {
    const raw = await this.#readDoc(cacheKey);
    return (raw?.convId as string) ?? null;
  }

  /** When the key was last cached, the grounding signature, and the feedback metadata it was
   *  cached with. */
  async getCacheMeta(cacheKey: string): Promise<CacheMeta> {
    const raw = await this.#readDoc(cacheKey);
    return {
      at: raw?.at ?? null,
      signature: raw?.signature ?? null,
      responseId: raw?.responseId ?? null,
      usage: raw?.usage ?? null,
    };
  }

  /** Persists the full item list under the key, plus the grounding signature and feedback
   *  metadata (`responseId`/`usage`) the caller generated it with, if any. No-op on missing /
   *  empty inputs. */
  async cacheItems(cacheKey: string, convId: string, items: AiConversationItem[], meta?: CacheFeedbackMeta): Promise<void> {
    if (!cacheKey || !convId || !Array.isArray(items) || items.length === 0) return;
    try {
      const payload: CachedConversation = {
        convId,
        items,
        at: new Date().toISOString(),
        signature: meta?.signature,
        responseId: meta?.responseId,
        usage: meta?.usage,
      };
      await this.#disk.updateOrInsert(cacheKey, () => ({ raw: payload }));
    } catch (e) {
      console.error('SummaryConversationCache cacheItems:', e);
    }
  }

  /**
   * Resolve a summary conversation's items: local cache → Foundry (by convId,
   * caching on success) → null. Returns the chronological item list, or null
   * when there is genuinely nothing to resolve (no cache and no/dead conv id).
   */
  async resolve(
    category: AgentCategory,
    convId: string | null,
    cacheKey: string,
  ): Promise<AiConversationItem[] | null> {
    const local = await this.#getCachedItems(cacheKey);
    if (local) return local;
    if (!convId) return null;
    try {
      const conv = await this.#ai.resumeConversation(category, convId);
      const items = await conv.listItems();
      if (items.length) await this.cacheItems(cacheKey, convId, items);
      return items.length ? items : null;
    } catch (e) {
      console.warn('SummaryConversationCache resolve: Foundry fetch failed:', e);
      return null;
    }
  }
}
